import { useMutation } from '@tanstack/react-query';

import API from '@api/API';

interface DynamicActionPayload {
  action: string;
  payload?: any;
}

export const useDynamicActionMutation = () => {
  const { isPending: isSubmitting, mutateAsync } = useMutation({
    mutationFn: (payload: DynamicActionPayload) =>
      API.post<{ to?: string }, DynamicActionPayload>(`/v1/app/action`, payload),
  });

  return {
    isSubmitting,
    dynamicActionAsync: mutateAsync,
  };
};

function uploadVideoWithProgress(
  file: File,
  uploadUrl: string,
  onProgress?: (formattedNumber: string) => void,
) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', 'video/mp4');

    // Track upload progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === 'function') {
        const percent = (event.loaded / event.total) * 100;
        onProgress(percent.toFixed(2)); // e.g., 32.15
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve({ success: true });
      } else {
        reject(new Error(`Upload failed: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));

    xhr.send(file);
  });
}

export const useVideoUploadMutation = () => {
  const { isPending: isUploading, mutateAsync } = useMutation({
    mutationFn: async ({
      title,
      file,
      onProgress,
    }: {
      title: string;
      file: File;
      onProgress?: (formattedNumber: string) => void;
    }) => {
      const { videoId, uploadUrl } = await API.post<
        { videoId: string; uploadUrl: string },
        { title: string }
      >(`/v1/bunny/video/upload-url`, { title });

      if (videoId && uploadUrl) {
        await uploadVideoWithProgress(file, uploadUrl, onProgress);
      }
    },
  });

  return {
    isUploading,
    uploadVideoAsync: mutateAsync,
  };
};
